import React, { useState, useMemo, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import VehicleSelectionModal from './VehicleSelectionModal';
import { parseDateOnlyLocal } from '../utils/helpers';

const MiniCalendar = ({ onOpenVehicleDetail, currentPage }) => {
  const { vehicles } = useApp();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [modalVehicles, setModalVehicles] = useState([]);
  const [modalDate, setModalDate] = useState('');

  // Exclude archived vehicles from calendar
  const activeVehicles = useMemo(
    () => vehicles.filter((veh) => veh.status !== 'Archive' && !veh.deletedAt && !veh.collected),
    [vehicles]
  );

  // Close modal when page changes
  useEffect(() => {
    setShowVehicleModal(false);
  }, [currentPage]);

  const monthYear = currentDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - startDate.getDay());

    const dayHeaders = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    const days = [];

    // Add day headers
    dayHeaders.forEach(day => {
      days.push({ type: 'header', label: day });
    });

    // Pre-calculate vehicle dates and counts for this month (excluding archived)
    const vehicleDateCounts = {};
    activeVehicles.forEach(veh => {
      const arrivalDate = veh.receivedDate || veh.dropOffDate;
      if (arrivalDate) {
        const arrival = parseDateOnlyLocal(arrivalDate);
        if (arrival) {
          arrival.setHours(0, 0, 0, 0);
          if (arrival.getMonth() === month && arrival.getFullYear() === year) {
            const dateKey = arrival.getDate();
            vehicleDateCounts[dateKey] = (vehicleDateCounts[dateKey] || 0) + 1;
          }
        }
      }
      
      if (veh.collectionDate) {
        const collection = parseDateOnlyLocal(veh.collectionDate);
        if (collection) {
          collection.setHours(0, 0, 0, 0);
          if (collection.getMonth() === month && collection.getFullYear() === year) {
            const dateKey = collection.getDate();
            vehicleDateCounts[dateKey] = (vehicleDateCounts[dateKey] || 0) + 1;
          }
        }
      }
    });

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const loopDate = new Date(startDate);

    for (let i = 0; i < 42; i++) {
      const isOtherMonth = loopDate.getMonth() !== month;
      const isToday = loopDate.getTime() === today.getTime();
      const vehicleCount = !isOtherMonth ? (vehicleDateCounts[loopDate.getDate()] || 0) : 0;
      const hasVehicles = vehicleCount > 0;

      days.push({
        type: 'day',
        date: new Date(loopDate),
        dayNumber: loopDate.getDate(),
        isOtherMonth,
        isToday,
        hasVehicles,
        vehicleCount
      });

      loopDate.setDate(loopDate.getDate() + 1);
    }

    return days;
  }, [activeVehicles, currentDate]);

  const handleDayClick = (day) => {
    if (onOpenVehicleDetail && day.hasVehicles && !day.isOtherMonth) {
      // Find vehicles for this date (excluding archived)
      const dayVehicles = activeVehicles.filter(veh => {
        const arrivalDate = veh.receivedDate || veh.dropOffDate;
        if (arrivalDate) {
          const arrival = parseDateOnlyLocal(arrivalDate);
          if (arrival) {
            arrival.setHours(0, 0, 0, 0);
            if (arrival.getTime() === day.date.getTime()) return true;
          }
        }
        if (veh.collectionDate) {
          const collection = parseDateOnlyLocal(veh.collectionDate);
          if (collection) {
            collection.setHours(0, 0, 0, 0);
            if (collection.getTime() === day.date.getTime()) return true;
          }
        }
        return false;
      });
      
      if (dayVehicles.length === 1) {
        // Single vehicle - open directly
        const vehicleIndex = vehicles.findIndex(v => v.reg === dayVehicles[0].reg);
        if (vehicleIndex !== -1) {
          onOpenVehicleDetail(vehicleIndex);
        }
      } else if (dayVehicles.length > 1) {
        // Multiple vehicles - show selection modal (add eventType for Drop-off vs Collection)
        const dayStart = day.date.getTime();
        const withEventType = dayVehicles.map((veh) => {
          const arrivalDate = veh.receivedDate || veh.dropOffDate;
          let eventType = 'collection';
          if (arrivalDate) {
            const d = parseDateOnlyLocal(arrivalDate);
            if (d) {
              d.setHours(0, 0, 0, 0);
              if (d.getTime() === dayStart) eventType = 'arrival';
            }
          }
          return { ...veh, eventType };
        });
        setModalVehicles(withEventType);
        setModalDate(day.date.toLocaleDateString('en-GB'));
        setShowVehicleModal(true);
      }
    }
  };

  const handleVehicleSelect = (selectedVehicle) => {
    const vehicleIndex = vehicles.findIndex(v => v.reg === selectedVehicle.reg);
    if (vehicleIndex !== -1) {
      onOpenVehicleDetail(vehicleIndex);
    }
  };

  return (
    <div className="mini-calendar">
      <div className="mini-calendar-header">
        <button 
          type="button" 
          className="mini-calendar-nav-btn"
          onClick={() => {
            const newDate = new Date(currentDate);
            newDate.setMonth(newDate.getMonth() - 1);
            setCurrentDate(newDate);
          }}
        >
          ‹
        </button>
        <h3>{monthYear}</h3>
        <button 
          type="button" 
          className="mini-calendar-nav-btn"
          onClick={() => {
            const newDate = new Date(currentDate);
            newDate.setMonth(newDate.getMonth() + 1);
            setCurrentDate(newDate);
          }}
        >
          ›
        </button>
      </div>
      <div className="mini-calendar-grid">
        {calendarDays.map((day, idx) => {
          if (day.type === 'header') {
            return (
              <div key={idx} className="mini-calendar-day-header">
                {day.label}
              </div>
            );
          }

          return (
            <div
              key={idx}
              className={`mini-calendar-day ${day.isOtherMonth ? 'other-month' : ''} ${day.isToday ? 'today' : ''} ${day.hasVehicles ? 'has-vehicles' : ''}`}
              onClick={() => handleDayClick(day)}
              style={{ cursor: day.hasVehicles ? 'pointer' : 'default' }}
            >
              <span className="day-number">{day.dayNumber}</span>
              {day.hasVehicles && (
                <div className="vehicle-indicators" aria-label={`${day.vehicleCount} vehicles`}>
                  {Array.from({ length: Math.min(day.vehicleCount, 5) }, (_, i) => (
                    <div key={i} className="vehicle-dot" />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <VehicleSelectionModal
        isOpen={showVehicleModal}
        onClose={() => setShowVehicleModal(false)}
        vehicles={modalVehicles}
        date={modalDate}
        onSelectVehicle={handleVehicleSelect}
        title="Select Vehicle to View"
      />
    </div>
  );
};

export default MiniCalendar;

